import type { UseQueryOptions } from "@pinia/colada";

import { useQuery } from "@pinia/colada";
import { defineStore } from "pinia";
import { computed, watchEffect } from "vue";

import type { LocaleMorpheus } from "@solvari/translations";

import { addUserDataGtmEvent } from "@solvari/argus";
import {
  getEnv,
  HTTPError,
  kyNoCredentials,
  networkErrorToSentry,
} from "@solvari/utils";

function useUserQuery() {
  return {
    key: () => ["auth", "user"],
    query: () => {
      return kyNoCredentials
        .get(`${getEnv().network.api}/v3/my/user`, { credentials: "include" })
        .json<App.DTO.Customer.UserResource>();
    },
    staleTime: 60 * 60_000,
  } satisfies UseQueryOptions<App.DTO.Customer.UserResource>;
}

const useAuthStore = defineStore("auth", () => {
  const { data: user, error, isLoading, refresh } = useQuery(useUserQuery());

  watchEffect(() => {
    if (!error.value) return;

    if (
      error.value instanceof HTTPError &&
      error.value.response.status === 401
    ) {
      const redirectUrl = getEnv().network.auth.baseWithPrefix;
      if (!window.location.href.includes(redirectUrl)) {
        window.location.href = redirectUrl;
      }
    } else {
      networkErrorToSentry(error.value);
    }
  });

  function can(permissions: string[] | string): boolean {
    if (!user.value) {
      return false;
    }
    if (Array.isArray(permissions)) {
      return permissions.every((permission) => can(permission));
    }

    return user.value.permissions.includes(permissions);
  }

  const userRoles = computed(() =>
    user.value?.divisions ? new Set(user.value.divisions) : new Set<string>(),
  );

  return {
    user,
    fetchingUser: isLoading,
    refresh,
    userId: computed(() => user.value?.id ?? null),
    customerUuid: computed(() => user.value?.pro?.uuid ?? null),
    customerId: computed(() => user.value?.pro?.id ?? null),
    locale: computed<LocaleMorpheus | undefined>(() => user.value?.pro?.locale),
    can,
    isAny: (roles: string[]) => roles.some((role) => userRoles.value.has(role)),
  };
});

function startUserDataGtmWatcher() {
  const { data: user, refresh } = useQuery(useUserQuery());
  void refresh();
  watchEffect(() => {
    if (user.value?.id) {
      void addUserDataGtmEvent({
        userId: user.value.id,
        customerId: user.value.pro?.id,
      });
    }
  });
}

export { startUserDataGtmWatcher, useAuthStore };
